/**
 * PATH: packages/api/src/view-preferences/createViewPreferenceStore.ts
 * FILE_ID: API-VIEWPREF-STORE-001
 * PURPOSE: Own one durable preference scope from local draft through revisioned sync.
 * ROLE IN ARCHITECTURE: Framework-neutral state node above injected cache and transport.
 * MAIN EXPORTS: createViewPreferenceStore, ViewPreferenceConflictError.
 * NON-RESPONSIBILITIES: Does not choose browser storage, HTTP auth, or domain codecs.
 * NOTES FOR FUTURE AI: Retry a 412 only when conflictPolicy is retry-local.
 */

import {
  VIEW_PREFERENCE_MAX_STATE_BYTES,
  normalizeViewPreferenceEtag,
  viewPreferenceCacheKey,
} from './contract'
import {
  ViewPreferenceConflictError,
  ViewPreferenceStateTooLargeError,
} from './errors'
import type {
  CreateViewPreferenceStoreOptions,
  ViewPreferenceSnapshot,
  ViewPreferenceStore,
  ViewPreferenceTelemetryEvent,
} from './storeContract'

export { ViewPreferenceConflictError, ViewPreferenceStateTooLargeError } from './errors'

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function byteSize(value: object): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength
}

function now(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now()
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * Create the sole runtime for one tenant, user, and opaque preference scope.
 *
 * Inputs: validated domain codec plus injected local cache and remote transport.
 * Output: subscribable optimistic store with explicit hydrate/conflict operations.
 * Side effects: cache writes, timers, transport calls, and structured telemetry.
 * Failure modes: transport/size failures become snapshot state; 412 keeps the draft.
 */
export function createViewPreferenceStore<T extends object>(
  options: CreateViewPreferenceStoreOptions<T>,
): ViewPreferenceStore<T> {
  const key = viewPreferenceCacheKey(options.identity, options.scopeKey)
  const listeners = new Set<() => void>()
  const cached = options.cache.read(key)
  let snapshot: ViewPreferenceSnapshot<T> = {
    value: cached === undefined ? clone(options.defaults) : options.coerce(cached),
    hydrated: false,
    hasRemoteValue: false,
    syncState: 'idle',
    conflict: null,
    error: null,
  }
  let etag: string | null = null
  let remoteSchemaVersion: number | null = null
  let generation = 0
  let hydratePromise: Promise<void> | null = null
  let saveTimer: ReturnType<typeof setTimeout> | null = null
  let pendingDelete = false
  let destroyed = false

  const actionId = () =>
    options.createActionId?.() ??
    `viewpref-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const emit = (event: ViewPreferenceTelemetryEvent) => options.telemetry?.(event)
  const adoptRevision = (nextEtag: string, nextSchema: number) => {
    etag = normalizeViewPreferenceEtag(nextEtag) ?? nextEtag
    remoteSchemaVersion = nextSchema
  }
  const publish = (next: ViewPreferenceSnapshot<T>) => {
    snapshot = next
    listeners.forEach((listener) => listener())
  }
  const cacheAndPublish = (value: T, next: Partial<ViewPreferenceSnapshot<T>>) => {
    options.cache.write(key, value)
    publish({ ...snapshot, ...next, value })
  }

  const hydrate = async () => {
    if (snapshot.hydrated || destroyed) return
    if (hydratePromise) return hydratePromise
    const started = now()
    const id = actionId()
    const startedGeneration = generation
    publish({ ...snapshot, syncState: snapshot.syncState === 'pending' ? 'pending' : 'hydrating' })
    emit({
      component: 'view-preference-store', eventType: 'start', operation: 'hydrate',
      actionId: id, scopeKey: options.scopeKey, status: 'hydrating',
    })
    hydratePromise = options.transport.get({ scopeKey: options.scopeKey, actionId: id })
      .then((envelope) => {
        adoptRevision(envelope.etag, envelope.schemaVersion)
        const remote = options.coerce(envelope.state)
        const hasRemoteValue =
          !!envelope.state && typeof envelope.state === 'object' &&
          Object.keys(envelope.state as object).length > 0
        if (generation === 0 && startedGeneration === 0) {
          cacheAndPublish(remote, {
            hydrated: true, hasRemoteValue, syncState: 'synced',
            conflict: null, error: null,
          })
        } else {
          publish({ ...snapshot, hydrated: true, hasRemoteValue, syncState: 'pending' })
        }
        emit({
          component: 'view-preference-store', eventType: 'end', operation: 'hydrate',
          actionId: id, scopeKey: options.scopeKey, status: snapshot.syncState,
          durationMs: now() - started,
        })
      })
      .catch((error) => {
        publish({
          ...snapshot, hydrated: true,
          syncState: generation > startedGeneration ? 'pending' : 'error',
          error: errorMessage(error),
        })
        emit({
          component: 'view-preference-store', eventType: 'error', operation: 'hydrate',
          actionId: id, scopeKey: options.scopeKey, status: snapshot.syncState,
          durationMs: now() - started, error: errorMessage(error),
        })
      })
      .finally(() => {
        hydratePromise = null
      })
    return hydratePromise
  }

  const flush = async () => {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = null
    if (destroyed || snapshot.conflict) return
    if (!snapshot.hydrated) await hydrate()
    if (destroyed || snapshot.conflict) return
    if (!etag) {
      publish({ ...snapshot, syncState: 'error', error: 'Preference revision unavailable' })
      return
    }
    const operation = pendingDelete ? 'delete' : 'patch'
    const sentGeneration = generation
    const sentValue = snapshot.value
    const sentBytes = byteSize(sentValue)
    const id = actionId()
    const started = now()
    try {
      if (
        operation === 'patch' &&
        remoteSchemaVersion !== null &&
        remoteSchemaVersion > options.schemaVersion
      ) {
        throw new Error(
          `Preference schema ${remoteSchemaVersion} is newer than client schema ${options.schemaVersion}`,
        )
      }
      if (sentBytes > VIEW_PREFERENCE_MAX_STATE_BYTES) {
        throw new ViewPreferenceStateTooLargeError(sentBytes)
      }
      emit({
        component: 'view-preference-store', eventType: 'start', operation,
        actionId: id, scopeKey: options.scopeKey, status: 'pending', byteSize: sentBytes,
      })
      const sendMutation = (currentEtag: string) =>
        operation === 'delete'
          ? options.transport.delete({
              scopeKey: options.scopeKey,
              ifMatch: currentEtag,
              schemaVersion: options.schemaVersion,
              actionId: id,
            })
          : options.transport.patch({
              scopeKey: options.scopeKey,
              state: sentValue as Record<string, unknown>,
              ifMatch: currentEtag,
              schemaVersion: options.schemaVersion,
              actionId: id,
            })
      let envelope
      try {
        envelope = await sendMutation(etag)
      } catch (error) {
        if (
          !(error instanceof ViewPreferenceConflictError) ||
          options.conflictPolicy !== 'retry-local' ||
          !error.envelope.etag ||
          error.envelope.etag === etag
        ) {
          throw error
        }
        adoptRevision(error.envelope.etag, error.envelope.schemaVersion)
        if (!etag) throw error
        envelope = await sendMutation(etag)
      }
      adoptRevision(envelope.etag, envelope.schemaVersion)
      pendingDelete = false
      if (generation === sentGeneration) {
        const value = operation === 'delete' ? clone(options.defaults) : options.coerce(envelope.state)
        cacheAndPublish(value, { syncState: 'synced', conflict: null, error: null })
      } else {
        publish({ ...snapshot, syncState: 'pending' })
        schedule()
      }
      emit({
        component: 'view-preference-store', eventType: 'end', operation,
        actionId: id, scopeKey: options.scopeKey, status: snapshot.syncState,
        durationMs: now() - started, byteSize: sentBytes,
      })
    } catch (error) {
      if (error instanceof ViewPreferenceConflictError) {
        const remote = options.coerce(error.envelope.state)
        publish({
          ...snapshot, syncState: 'conflict', error: null,
          conflict: {
            remote, etag: error.envelope.etag,
            schemaVersion: error.envelope.schemaVersion,
            status: error.status,
            code: error.code,
            message: error.message,
          },
        })
        emit({
          component: 'view-preference-store', eventType: 'conflict', operation,
          actionId: id, scopeKey: options.scopeKey, status: 'conflict',
          durationMs: now() - started, byteSize: sentBytes,
        })
        return
      }
      publish({ ...snapshot, syncState: 'error', error: errorMessage(error) })
      emit({
        component: 'view-preference-store', eventType: 'error', operation,
        actionId: id, scopeKey: options.scopeKey, status: 'error',
        durationMs: now() - started, byteSize: sentBytes, error: errorMessage(error),
      })
    }
  }

  const schedule = (delay = options.debounceMs ?? 700) => {
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(() => void flush(), Math.max(0, delay))
  }

  const unsubscribeCache = options.cache.subscribe?.(key, (raw) => {
    if (destroyed || snapshot.syncState === 'pending' || snapshot.syncState === 'conflict') return
    const value = options.coerce(raw)
    publish({ ...snapshot, value })
  })

  return {
    key,
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    hydrate,
    setValue(next, saveOptions) {
      generation += 1
      pendingDelete = false
      if (snapshot.conflict) {
        adoptRevision(snapshot.conflict.etag, snapshot.conflict.schemaVersion)
      }
      cacheAndPublish(options.coerce(next), {
        syncState: 'pending', conflict: null, error: null,
      })
      schedule(saveOptions?.debounceMs)
    },
    cacheValue(next) {
      cacheAndPublish(options.coerce(next), {})
    },
    reset(saveOptions) {
      generation += 1
      pendingDelete = true
      if (snapshot.conflict) {
        adoptRevision(snapshot.conflict.etag, snapshot.conflict.schemaVersion)
      }
      cacheAndPublish(clone(options.defaults), {
        syncState: 'pending', conflict: null, error: null,
      })
      schedule(saveOptions?.debounceMs)
    },
    flush,
    retry() {
      if (snapshot.syncState !== 'error') return
      publish({ ...snapshot, syncState: 'pending', error: null })
      schedule(0)
    },
    retryConflict() {
      if (!snapshot.conflict) return
      adoptRevision(snapshot.conflict.etag, snapshot.conflict.schemaVersion)
      publish({ ...snapshot, syncState: 'pending', conflict: null })
      schedule(0)
    },
    discardConflict() {
      if (!snapshot.conflict) return
      adoptRevision(snapshot.conflict.etag, snapshot.conflict.schemaVersion)
      generation += 1
      cacheAndPublish(snapshot.conflict.remote, {
        syncState: 'synced', conflict: null, error: null,
      })
    },
    destroy() {
      destroyed = true
      if (saveTimer) clearTimeout(saveTimer)
      unsubscribeCache?.()
      listeners.clear()
    },
  }
}
